import { Link, useLocation } from 'react-router-dom';
import { ClipboardList, ShoppingCart } from 'lucide-react';
import { useAuth } from '@/core/auth/useAuth';
import { useModuleAccess } from '@/core/permissions/useModuleAccess';
import { isAdminAreaOnlyUser } from '@/core/permissions/adminAreaAccess';
import { cn } from '@/lib/utils';

interface AdminShopAreaSwitcherProps {
  /** 'shop' usa i token cromatici dell'header shop */
  variant?: 'default' | 'shop';
  className?: string;
}

/**
 * Passaggio rapido Amministrazione ↔ Shop per gli utenti dell'area amministrativa
 * a cui è stato concesso il modulo "shop". Nascosto per tutti gli altri ruoli.
 */
export function AdminShopAreaSwitcher({ variant = 'default', className }: AdminShopAreaSwitcherProps) {
  const { user, hasRole } = useAuth();
  const { hasModule } = useModuleAccess();
  const location = useLocation();

  if (!user || !isAdminAreaOnlyUser(hasRole) || !hasModule('shop')) return null;

  const isInShop = location.pathname.startsWith('/shop');
  const target = isInShop ? '/amministrazione' : '/shop';
  const label = isInShop ? 'Amministrazione' : 'Shop';
  const Icon = isInShop ? ClipboardList : ShoppingCart;

  return (
    <Link
      to={target}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full border transition-colors shrink-0',
        variant === 'shop'
          ? 'border-[var(--shop-ink-200)] text-[var(--shop-ink-700)] hover:bg-[var(--shop-ink-100)]'
          : 'border-border bg-background hover:bg-accent hover:border-primary',
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </Link>
  );
}
